import React from 'react';
import { Tag } from 'lucide-react';
import { SMS_PROMO, isSmsPromoActive } from '../config/promo';

interface PromoBannerProps {
  // 'strip': thin fixed bar at the very top of the agent store.
  // 'card':  prominent inline callout on SMS product pages.
  variant: 'strip' | 'card';
}

// Advertises the SMS leads sale. Renders nothing once the sale window closes,
// so it can be left mounted year-round without manual cleanup.
const PromoBanner: React.FC<PromoBannerProps> = ({ variant }) => {
  if (!isSmsPromoActive()) return null;

  if (variant === 'strip') {
    return (
      <div
        className="fixed top-0 left-0 right-0 z-[60] bg-navy-600 text-white"
        role="region"
        aria-label="Promotional offer"
      >
        <div className="max-w-7xl mx-auto h-9 px-4 flex items-center justify-center">
          {/* Full message on tablet and up */}
          <span className="hidden sm:flex items-center gap-2 text-sm">
            <span className="font-semibold tracking-tight">{SMS_PROMO.headline}</span>
            <span className="text-navy-300" aria-hidden="true">·</span>
            <span className="text-navy-100">
              Use code <span className="font-bold text-white">{SMS_PROMO.code}</span> at checkout
            </span>
          </span>
          {/* Condensed message on mobile so it stays on one line */}
          <span className="flex sm:hidden items-center gap-1.5 text-xs">
            <span className="font-semibold tracking-tight">20% OFF All SMS Leads</span>
            <span className="text-navy-300" aria-hidden="true">·</span>
            <span className="text-navy-100">
              Code <span className="font-bold text-white">{SMS_PROMO.code}</span>
            </span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mb-4 flex items-center gap-3 rounded-xl border border-navy-200 bg-navy-50 px-4 py-3 sm:px-5 sm:py-4"
      role="region"
      aria-label="Promotional offer"
    >
      <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg bg-navy-600 text-white">
        <Tag className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-sm sm:text-base font-bold text-navy-800">{SMS_PROMO.headline}</p>
        <p className="text-xs sm:text-sm text-navy-600">
          Use code <span className="font-bold text-navy-800">{SMS_PROMO.code}</span> at checkout
        </p>
      </div>
    </div>
  );
};

export default PromoBanner;
